/**
 * Role selector dropdown.
 *
 * Shows the user's assigned roles within the current organization,
 * allowing them to filter tiered data by role.
 *
 * Features:
 * - "All Roles" option at top (shows all accessible tiers)
 * - Lists user's assigned roles with priority sorting
 * - Single role: displays as badge, no dropdown needed
 * - No roles: hidden (user only sees org + personal tier)
 */

import { useState } from "react";
import { ChevronDown, Users2, Check, Layers } from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@cloch-ai/ui";
import { useRoleContext } from "../providers/RoleProvider";
import { cn } from "../lib/utils";

interface RoleSelectorProps {
  /** Additional class names */
  className?: string;
  /** Compact mode for header */
  compact?: boolean;
}

/**
 * Role selector component.
 */
export function RoleSelector({ className, compact = false }: RoleSelectorProps) {
  const {
    currentRole,
    roles,
    isLoading,
    status,
    error,
    isAllRolesView,
    setCurrentRole,
    refreshRoles,
  } = useRoleContext();
  const [switching, setSwitching] = useState(false);

  const handleRoleSelect = async (roleId: string | null) => {
    if (roleId === currentRole?.id || (roleId === null && isAllRolesView)) {
      return;
    }
    setSwitching(true);
    try {
      await setCurrentRole(roleId);
    } finally {
      setSwitching(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div
        data-testid="role-selector-loading"
        className={cn("h-9 w-24 bg-muted animate-pulse rounded-md", className)}
      />
    );
  }

  // SYN-3367 — the server is rate limiting us and the provider will ask again by itself.
  // Calm, and not a sign-out: nothing is wrong with the person or their session.
  if (status === "busy") {
    return (
      <Button
        variant="outline"
        size={compact ? "sm" : "default"}
        className={cn("justify-between gap-2 min-w-[120px] opacity-70", className)}
        data-testid="role-selector-busy"
        title="The service is busy. Your roles will load in a moment."
        disabled
      >
        <div className="flex items-center gap-2 truncate">
          <Layers className="h-4 w-4 shrink-0 opacity-60 animate-pulse" />
          <span className="truncate max-w-[110px]">Busy, retrying…</span>
        </div>
      </Button>
    );
  }

  // SYN-3115 — we could not read this person's roles. That is NOT "they have none", and
  // it must not be dressed as it: an empty list here used to render "No Roles", which is a
  // statement about the person that nobody had established. This state says what actually
  // happened and offers the action that can fix it.
  if (status === "unavailable" || status === "forbidden") {
    return (
      <Button
        variant="outline"
        size={compact ? "sm" : "default"}
        className={cn("justify-between gap-2 min-w-[120px]", className)}
        data-testid="role-selector-unavailable"
        title={error ?? "We couldn't load your roles. Refresh and try again."}
        onClick={() => {
          void refreshRoles();
        }}
      >
        <div className="flex items-center gap-2 truncate">
          <Layers className="h-4 w-4 shrink-0 opacity-60" />
          <span className="truncate max-w-[110px]">Roles unavailable</span>
        </div>
      </Button>
    );
  }

  // No roles defined - show disabled dropdown. Reachable only when status is "ready", so
  // this is a fact about the person and not about a failed read.
  if (roles.length === 0) {
    return (
      <Button
        variant="outline"
        size={compact ? "sm" : "default"}
        className={cn("justify-between gap-2 min-w-[120px] opacity-50", className)}
        disabled
      >
        <div className="flex items-center gap-2 truncate">
          <Layers className="h-4 w-4 shrink-0" />
          <span className="truncate max-w-[100px]">No Roles</span>
        </div>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={compact ? "sm" : "default"}
          className={cn("justify-between gap-2 min-w-[120px]", className)}
          disabled={switching}
        >
          <div className="flex items-center gap-2 truncate">
            {isAllRolesView ? (
              <>
                <Layers className="h-4 w-4 shrink-0" />
                <span className="truncate max-w-[100px]">All Roles</span>
              </>
            ) : (
              <>
                <Users2 className="h-4 w-4 shrink-0" />
                <span className="truncate max-w-[100px]">{currentRole?.name ?? "Select role"}</span>
              </>
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Users2 className="h-4 w-4" />
          Role Filter
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* "All Roles" option */}
        <DropdownMenuItem
          onClick={() => handleRoleSelect(null)}
          className={cn(
            "flex items-center justify-between gap-2 cursor-pointer",
            isAllRolesView && "bg-accent",
          )}
        >
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <span>All Roles</span>
          </div>
          {isAllRolesView && <Check className="h-4 w-4" />}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* User's assigned roles */}
        {roles.map((role) => {
          const isActive = role.id === currentRole?.id;
          return (
            <DropdownMenuItem
              key={role.id}
              onClick={() => handleRoleSelect(role.id)}
              className={cn(
                "flex items-center justify-between gap-2 cursor-pointer",
                isActive && "bg-accent",
              )}
            >
              <span className="truncate">{role.name}</span>
              {isActive && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
